import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDownRight, MessageSquareText } from "lucide-react";
import { PHONE_TEL, PHONE_SMS, PHONE_DISPLAY } from "../site";

const HERO_IMG =
  "https://images.pexels.com/photos/20306314/pexels-photo-20306314.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "~20 min", label: "avg. response" },
  { value: "24/7", label: "dispatch" },
  { value: "All", label: "makes & models" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <div
        className="absolute inset-0 bg-[linear-gradient(to_right,#e4e4e7_1px,transparent_1px),linear-gradient(to_bottom,#e4e4e7_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100/50 via-zinc-50 to-zinc-50"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-44 pb-20 md:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-end">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 mb-6"
            data-testid="hero-overline"
          >
            Automotive & Residential Locksmith — Winnipeg, MB
          </motion.p>

          <h1 className="font-heading font-bold tracking-tighter leading-[0.95] text-5xl md:text-6xl lg:text-7xl text-zinc-950" data-testid="hero-headline">
            <MaskedLine delay={0.25}>Locked out?</MaskedLine>
            <MaskedLine delay={0.4}>
              We come to <span className="text-brand">you.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 max-w-md text-base leading-relaxed text-zinc-600"
            data-testid="hero-subcopy"
          >
            Car and home lockouts, key fob programming and transponder keys —
            done on the spot, anywhere in Winnipeg. Day, night, -40°. One call
            and a fully equipped van is on its way.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={PHONE_TEL}
              data-testid="hero-call-now-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-alert px-7 py-4 text-base font-semibold text-white shadow-[0_0_28px_rgba(255,59,48,0.35)] hover:bg-red-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-200 focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <Phone className="h-5 w-5" />
              Call {PHONE_DISPLAY}
            </a>
            <a
              href={PHONE_SMS}
              data-testid="hero-text-us-button"
              className="inline-flex items-center gap-2.5 rounded-full border border-zinc-300 bg-white/70 backdrop-blur px-7 py-4 text-base font-semibold text-zinc-950 hover:border-zinc-950 transition-[border-color] duration-200 focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <MessageSquareText className="h-5 w-5" />
              Text us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-14 flex gap-10 md:gap-14"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-mono text-2xl md:text-3xl font-medium text-zinc-950">{s.value}</div>
                <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-3xl overflow-hidden border border-zinc-200 shadow-[0_32px_80px_rgba(0,0,0,0.12)] aspect-[4/5]">
            <motion.img
              src={HERO_IMG}
              alt="Automotive locksmith technician at work on a vehicle"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-full w-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/40 via-transparent to-transparent" aria-hidden="true" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.2 }}
            className="absolute -bottom-6 -left-4 md:-left-10 bg-white/80 backdrop-blur-xl border border-white/50 shadow-[0_8px_32px_rgba(0,0,0,0.08)] rounded-2xl px-5 py-4 flex items-center gap-3"
            data-testid="hero-floating-badge"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/10">
              <ArrowDownRight className="h-5 w-5 text-brand" />
            </span>
            <div>
              <div className="text-sm font-semibold text-zinc-950">On-site in minutes</div>
              <div className="text-xs text-zinc-500">Keys cut & programmed curbside</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
